import React, { useState, useEffect } from "react";
import { Card, Row, Col, Spin, Alert, Pagination } from "antd";
import BannerCarousel from "../components/BannerCarousel";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/Home.css";
import { Contact } from "../components/Contact";
import AnimatedElement from "../components/AnimatedElement";
import { publicacoesAPI, parceirosAPI } from "../services/api";
import { useTranslation } from "react-i18next";

// Definição de constantes para paginação
const ITEMS_PER_PAGE_DESKTOP = 6;
const ITEMS_PER_PAGE_MOBILE = 3;

export const ReproducaoHumanaAssistida = () => {
  const { t, i18n } = useTranslation();
  const [activeTabRHA, setActiveTabRHA] = useState("como-funciona-rha");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [publicacoes, setPublicacoes] = useState([]);
  const [parceiros, setParceiros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingParceiros, setLoadingParceiros] = useState(false);
  const [error, setError] = useState(null);
  const [errorParceiros, setErrorParceiros] = useState(null);

  // Estado para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startDrag, setStartDrag] = useState({ x: 0, y: 0 });
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });

  useEffect(() => {
    fetchPublicacoes();
    fetchParceiros();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchPublicacoes = async () => {
    setLoading(true);
    try {
      const response = await publicacoesAPI.getAll("RHA");
      setPublicacoes(response.data);
      setError(null);
    } catch (err) {
      console.error("Erro ao carregar publicações:", err);
      setError(t("rha.errors.loadPublications"));
    } finally {
      setLoading(false);
    }
  };

  const fetchParceiros = async () => {
    setLoadingParceiros(true);
    try {
      const response = await parceirosAPI.getAll();
      // Filtramos apenas os parceiros relacionados ao RHA
      const parceirosRHA = response.data.filter(
        (parceiro) => parceiro.produto === "RHA"
      );
      setParceiros(parceirosRHA);
      setErrorParceiros(null);
    } catch (err) {
      console.error("Erro ao carregar parceiros:", err);
      setErrorParceiros(t("rha.errors.loadPartners"));
    } finally {
      setLoadingParceiros(false);
    }
  };
  // Função utilitária para truncar texto com reticências
  const truncateText = (text, maxLength = 300) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + "...";
  };

  // Função utilitária para truncar título com reticências
  const truncateTitle = (title, maxLength = 180) => {
    if (!title) return "";
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength).trim() + "...";
  };

  // Converter dados da API para o formato usado no componente
  const formatPublicacoes = (data) => {
    return data.map((pub) => ({
      id: pub._id,
      title: truncateTitle(pub.titulo),
      description: truncateText(pub.texto),
      image:
        pub.imagem && pub.imagem.startsWith("http")
          ? pub.imagem
          : `${
              process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "") ||
              "http://localhost:5000"
            }${pub.imagem}`,
      link: pub.link,
    }));
  };

  // Fallback para publicações estáticas caso a API falhe
  const publicacoesRHA = [].map((pub) => ({
    ...pub,
    title: truncateTitle(pub.title),
    description: truncateText(pub.description),
  }));

  // Fallback para parceiros estáticos caso a API falhe
  const parceirosEstaticos = [];

  // Determinar quais publicações mostrar
  const publicacoesParaMostrar =
    publicacoes.length > 0 ? formatPublicacoes(publicacoes) : publicacoesRHA;

  // Processar os parceiros
  const formatParceiros = (data) => {
    return data.map((parceiro) => ({
      _id: parceiro._id,
      nome: parceiro.nome,
      logo:
        parceiro.logo && parceiro.logo.startsWith("http")
          ? parceiro.logo
          : `${
              process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "") ||
              "http://localhost:5000"
            }${parceiro.logo}`,
      link: parceiro.link,
    }));
  };

  // Determinar quais parceiros mostrar
  const parceirosParaMostrar =
    parceiros.length > 0 ? formatParceiros(parceiros) : parceirosEstaticos;

  // Lógica para paginação (desktop e mobile)
  const itemsPerPage = isMobile
    ? ITEMS_PER_PAGE_MOBILE
    : ITEMS_PER_PAGE_DESKTOP;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Função para lidar com a mudança de página
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Rolagem suave para o topo da seção de publicações
    document.querySelector(".publicacoes-plataforma").scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Itens a serem exibidos na página atual (desktop e mobile)
  const paginatedPublicacoes = publicacoesParaMostrar.slice(
    startIndex,
    endIndex
  );

  // Verificar se deve mostrar carousel ou grid estático
  const shouldShowCarousel = parceirosParaMostrar.length >= 6;

  // Banner estático para Reprodução Humana Assistida
  const banner = [
    {
      title: "",
      subtitle: t("rha.banner.subtitle"),
      description: t("rha.banner.description"),
      image: "/assets/img/banners/RHA3.jpg",
      buttonText: t("rha.banner.buttonText"),
    },
  ];
  const accordionData = [
    {
      id: "como-funciona-rha",
      title: t("rha.accordion.howItWorks.title"),
      content: <p>{t("rha.accordion.howItWorks.content")}</p>,
      image:
        i18n.language === "en"
          ? "/assets/img/product-images/rha2.translated.jpg"
          : "/assets/img/product-images/rha2.jpg",
    },
    {
      id: "nossa-abordagem-rha",
      title: t("rha.accordion.ourApproach.title"),
      content: <p>{t("rha.accordion.ourApproach.content")}</p>,
      image:
        i18n.language === "en"
          ? "/assets/img/product-images/rha1.translated.jpg"
          : "/assets/img/product-images/rha1.jpg",
    },
    {
      id: "nossa-ciencia-rha",
      title: t("rha.accordion.ourScience.title"),
      content: (
        <>
          <p>{t("rha.accordion.ourScience.content1")}</p>
          <p>{t("rha.accordion.ourScience.content2")}</p>
        </>
      ),
      image: "", // ajuste o caminho conforme necessário ou null
    },
  ];

  return (
    <div className="home" style={{ marginTop: 80 }}>
      {/* Lightbox para imagem ampliada com controles de zoom */}
      {lightboxImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.85)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            cursor: dragging ? "grabbing" : "zoom-out",
            overflow: "hidden",
          }}
          onClick={() => {
            setLightboxImage(null);
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
        >
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            style={{
              maxWidth: "96vw",
              maxHeight: "80vh",
              borderRadius: 12,
              boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
              transform: `scale(${zoom}) translate(${pan.x / zoom}px,${
                pan.y / zoom
              }px)`,
              transition: dragging ? "none" : "transform 0.2s",
              cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "default",
              touchAction: "none",
              background: "#fff",
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              if (zoom <= 1) return;
              setDragging(true);
              setStartDrag({ x: e.clientX, y: e.clientY });
              setStartPan({ ...pan });
            }}
            onMouseMove={(e) => {
              if (!dragging) return;
              setPan({
                x: startPan.x + (e.clientX - startDrag.x),
                y: startPan.y + (e.clientY - startDrag.y),
              });
            }}
            onMouseUp={() => setDragging(false)}
            onMouseLeave={() => setDragging(false)}
            onTouchStart={(e) => {
              if (zoom <= 1) return;
              setDragging(true);
              setStartDrag({
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
              });
              setStartPan({ ...pan });
            }}
            onTouchMove={(e) => {
              if (!dragging) return;
              setPan({
                x: startPan.x + (e.touches[0].clientX - startDrag.x),
                y: startPan.y + (e.touches[0].clientY - startDrag.y),
              });
            }}
            onTouchEnd={() => setDragging(false)}
            draggable={false}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 20,
              marginTop: 24,
              zIndex: 10000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}
              style={{
                fontSize: 28,
                background: "#1976d2", // azul principal
                color: "#fff",
                border: "none",
                borderRadius: 50,
                width: 54,
                height: 54,
                cursor: "pointer",
                boxShadow: "0 2px 12px rgba(25, 118, 210, 0.15)",
                transition: "background 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                outline: "none",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#1565c0")
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "#1976d2")}
              aria-label="Diminuir zoom"
            >
              –
            </button>
            <button
              onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
              style={{
                fontSize: 28,
                background: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 50,
                width: 54,
                height: 54,
                cursor: "pointer",
                boxShadow: "0 2px 12px rgba(25, 118, 210, 0.15)",
                transition: "background 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                outline: "none",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#1565c0")
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "#1976d2")}
              aria-label="Aumentar zoom"
            >
              +
            </button>
            <button
              onClick={() => {
                setLightboxImage(null);
                setZoom(1);
                setPan({ x: 0, y: 0 });
              }}
              style={{
                fontSize: 32,
                background: "#fff",
                color: "#1976d2",
                border: "2px solid #1976d2",
                borderRadius: 50,
                width: 54,
                height: 54,
                cursor: "pointer",
                boxShadow: "0 2px 12px rgba(25, 118, 210, 0.10)",
                transition: "background 0.2s, color 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                outline: "none",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#1976d2";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.color = "#1976d2";
              }}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <Navbar />
      <div id="banner-home">
        <BannerCarousel banners={banner} />
      </div>

      <section className="plataforma-rha">
        <div className="container">
          <AnimatedElement animation="fade-up">
            <div className="plataforma-content">
              <div className="plataforma-header">
                <h2>{t("rha.platform.title")}</h2>
                <p>{t("rha.platform.subtitle")}</p>
              </div>
              <Card className="plataforma-tabs-card">
                {isMobile ? (
                  <div className="faq-container">
                    {accordionData.map((item) => (
                      <div
                        key={item.id}
                        className={`faq-item ${
                          activeAccordion === item.id ? "active" : ""
                        }`}
                      >
                        <div
                          className="faq-question"
                          onClick={() =>
                            setActiveAccordion(
                              activeAccordion === item.id ? null : item.id
                            )
                          }
                        >
                          <h4>{item.title}</h4>
                          <div className="faq-icon">
                            <span
                              className={`faq-arrow ${
                                activeAccordion === item.id ? "rotated" : ""
                              }`}
                            >
                              ▼
                            </span>
                          </div>
                        </div>
                        <div
                          className={`faq-answer ${
                            activeAccordion === item.id ? "expanded" : ""
                          }`}
                        >
                          <div className="faq-answer-content">
                            {item.content}
                            {item.image && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  marginTop: 12,
                                  marginBottom: 12,
                                }}
                              >
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  style={{
                                    maxWidth: 320,
                                    width: "80vw",
                                    height: "auto",
                                    maxHeight: 140,
                                    objectFit: "contain",
                                    borderRadius: 8,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                                    cursor: "zoom-in",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLightboxImage({
                                      src: item.image,
                                      alt: item.title,
                                    });
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="plataforma-tabs">
                      <div
                        className={`tab-item ${
                          activeTabRHA === "como-funciona-rha" ? "active" : ""
                        }`}
                        onClick={() => setActiveTabRHA("como-funciona-rha")}
                      >
                        <h3>{t("rha.accordion.howItWorks.title")}</h3>
                      </div>
                      <div
                        className={`tab-item ${
                          activeTabRHA === "nossa-abordagem-rha" ? "active" : ""
                        }`}
                        onClick={() => setActiveTabRHA("nossa-abordagem-rha")}
                      >
                        <h3>{t("rha.accordion.ourApproach.title")}</h3>
                      </div>
                      <div
                        className={`tab-item ${
                          activeTabRHA === "nossa-ciencia-rha" ? "active" : ""
                        }`}
                        onClick={() => setActiveTabRHA("nossa-ciencia-rha")}
                      >
                        <h3>{t("rha.accordion.ourScience.title")}</h3>
                      </div>
                    </div>
                    <div className="plataforma-details">
                      {(() => {
                        const item = accordionData.find(
                          (i) => i.id === activeTabRHA
                        );
                        return (
                          <div className="tab-text-content">
                            {item.content}
                            {item.image && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  marginTop: 16,
                                }}
                              >
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  style={{ maxWidth: "130%" }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </>
                )}
              </Card>

              {/* Publicações RHA */}
              <div className="publicacoes-plataforma">
                <div className="publicacoes-header">
                  <h3>{t("rha.publications.title")}</h3>
                  <p>{t("rha.publications.subtitle")}</p>
                </div>

                {loading ? (
                  <div className="loading-container">
                    <Spin size="large" tip={t("rha.loading.publications")} />
                  </div>
                ) : error ? (
                  <Alert message={error} type="error" showIcon />
                ) : (
                  <>
                    <Row
                      gutter={[24, 24]}
                      style={{ margin: "auto" }}
                      className="publicacoes-grid"
                    >
                      {paginatedPublicacoes.map((publicacao) => (
                        <Col xs={24} md={12} lg={8} key={publicacao.id}>
                          <div className="publicacao-card-bg">
                            <img
                              src={publicacao.image}
                              alt={publicacao.title}
                              className="publicacao-card-image"
                              crossOrigin="anonymous"
                            />
                            <div className="publicacao-card-overlay">
                              <div className="publicacao-card-content">
                                <h4 className="publicacao-title">
                                  {publicacao.title}
                                </h4>
                                <p className="publicacao-description">
                                  {publicacao.description}
                                </p>
                                <a
                                  href={publicacao.link}
                                  className="publicacao-cta-btn"
                                >
                                  {t("rha.publications.readMore")} →
                                </a>
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}{" "}
                    </Row>
                    {publicacoesParaMostrar.length >
                      (isMobile
                        ? ITEMS_PER_PAGE_MOBILE
                        : ITEMS_PER_PAGE_DESKTOP) && (
                      <div
                        className="pagination-container"
                        style={{ textAlign: "center", marginTop: "30px" }}
                      >
                        <Pagination
                          current={currentPage}
                          total={publicacoesParaMostrar.length}
                          pageSize={
                            isMobile
                              ? ITEMS_PER_PAGE_MOBILE
                              : ITEMS_PER_PAGE_DESKTOP
                          }
                          onChange={handlePageChange}
                          showSizeChanger={false}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </AnimatedElement>
        </div>
      </section>

      {/* Nossos Parceiros */}
      <section id="nossos-parceiros" className="parceiros-section">
        <div className="container">
          <AnimatedElement animation="fade-up">
            <div className="parceiros-header">
              <h2>{t("rha.partners.title")}</h2>
              <p>{t("rha.partners.subtitle")}</p>
            </div>

            <div
              className={
                shouldShowCarousel
                  ? "parceiros-carousel"
                  : "parceiros-grid-static"
              }
            >
              {shouldShowCarousel ? (
                <div className="parceiros-track">
                  {loadingParceiros ? (
                    <div className="loading-container">
                      <Spin size="large" tip={t("rha.loading.partners")} />
                    </div>
                  ) : errorParceiros ? (
                    <Alert message={errorParceiros} type="error" showIcon />
                  ) : (
                    // Duplicar itens para efeito de loop contínuo
                    [...parceirosParaMostrar, ...parceirosParaMostrar].map(
                      (parceiro, index) => (
                        <div
                          className="parceiro-item"
                          key={`${parceiro._id}-${index}`}
                        >
                          <img
                            src={
                              parceiro.logo?.startsWith("http")
                                ? parceiro.logo
                                : `${
                                    process.env.REACT_APP_API_URL ||
                                    "http://localhost:5000"
                                  }${parceiro.logo}`
                            }
                            alt={parceiro.nome}
                            crossOrigin="anonymous"
                          />
                        </div>
                      )
                    )
                  )}
                </div>
              ) : (
                <div className="parceiros-static-grid">
                  {loadingParceiros ? (
                    <div className="loading-container">
                      <Spin size="large" tip={t("rha.loading.partners")} />
                    </div>
                  ) : errorParceiros ? (
                    <Alert message={errorParceiros} type="error" showIcon />
                  ) : (
                    parceirosParaMostrar.map((parceiro) => (
                      <div className="parceiro-item-static" key={parceiro._id}>
                        <img
                          src={
                            parceiro.logo?.startsWith("http")
                              ? parceiro.logo
                              : `${
                                  process.env.REACT_APP_API_URL ||
                                  "http://localhost:5000"
                                }${parceiro.logo}`
                          }
                          alt={parceiro.nome}
                          crossOrigin="anonymous"
                        />
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </AnimatedElement>
        </div>
      </section>

      <Contact />

      <Footer />
    </div>
  );
};
